import { Theme } from "@mui/material";
import { CellValueChangedEvent } from "ag-grid-community";
import { FieldArrayWithId } from "react-hook-form";
import { IAttachment } from "../../../common/models/IAttachment";
import { FormControls, IBaseFormControl, IFormCheckBox, IFormDate, IFormDateRange, IFormGrid, IFormLabel, IFormRadioGroup, IFormSection, IFormSelect, IFormTextField } from "../../../services/custom-forms-data-service/TemplateModels";
import { IFormInstanceData } from "../../../views/object-form-view/Models";
import { FormAttachmentActionCardEnum } from "../../forms/form-attachment-card/FormAttachmentCard";
import { CustomFormRunnerModeType } from "../CustomFormRunner";
import { CustomFormType } from "../CustomFormRunnerUtils";
import CustomFormAttachments from "./CustomFormAttachments";
import CustomFormCheckBox from "./CustomFormCheckBox";
import CustomFormDate from "./CustomFormDate";
import CustomFormDateRange from "./CustomFormDateRange";
import CustomFormGrid from "./CustomFormGrid";
import CustomFormLabel from "./CustomFormLabel";
import CustomFormRadioGroup from "./CustomFormRadioGroup";
import CustomFormSection from "./CustomFormSection";
import CustomFormSelect from "./CustomFormSelect";
import CustomFormTextField from "./CustomFormTextField";
import { SxProps } from "@mui/system";

import type { JSX } from "react";

export type CustomFormRenderControlProps = {
    mode?: CustomFormRunnerModeType;
    controls: FormControls[];
    instanceData?: IFormInstanceData;
    onDataChanged?: (event: CellValueChangedEvent) => void;
    inSection?: boolean;

    attachments?: FieldArrayWithId<CustomFormType, "attachments", "fieldKey">[],
    onAttachmentAction: (action: FormAttachmentActionCardEnum,
        index: number,
        attachment: IAttachment) => void,
    onOpenCamera: () => void

};

export default function CustomFormRenderControls({
    mode,
    controls,
    instanceData,
    onDataChanged,
    inSection,
    attachments,
    onAttachmentAction,
    onOpenCamera

}: CustomFormRenderControlProps) {

    const renderControl = (
        mode: CustomFormRunnerModeType | undefined,
        item: IBaseFormControl | IFormGrid | IFormSection | IFormLabel, 
        index: number,
        sx: SxProps<Theme> | undefined 
        ): JSX.Element | null => {

        switch (item.controlType) {
            case 'section':
                return CustomFormSection({
                    key: index, 
                    section: item as IFormSection, 
                    instanceData: instanceData, 
                    onDataChanged: onDataChanged,
                    mode: mode,
                    attachments: attachments,
                    onAttachmentAction: onAttachmentAction,
                    onOpenCamera: onOpenCamera 
                });
            case 'textfield':
                const textField = item as IFormTextField;
                return CustomFormTextField({
                    key: index, 
                    textField: {...textField, 
                        readOnly: mode === 'view' || textField.readOnly ? true : undefined,
                        numeric: textField.numeric || textField.valueType === 'number'
                    },
                    sx: sx
                });
    
            case 'checkbox':
                return CustomFormCheckBox({
                    key: index, 
                    checkbox: {...item as IFormCheckBox,
                        readOnly: mode === 'view' ? true : undefined
                    },
                    sx: sx
                });

            case 'radiogroup':
                return CustomFormRadioGroup({
                    key: index, 
                    radioButtonGroup: {...item as IFormRadioGroup,
                        readOnly: mode === 'view' ? true : undefined
                    },
                    sx: sx
                });
            
            case 'select':
                return CustomFormSelect({
                    key: index,
                    select: {...item as IFormSelect,
                        readOnly: mode === 'view' ? true : undefined
                    },
                    sx: sx
                });

            case 'date':
                return CustomFormDate({
                    key: index, 
                    date: {...item as IFormDate,
                        readOnly: mode === 'view' ? true : undefined
                    },
                    sx: sx
                });
            case 'daterange':
                return CustomFormDateRange({
                    key: index,
                    dateRange: {...item as IFormDateRange,
                        readOnly: mode === 'view' ? true : undefined
                    },
                    sx: sx
                });
   
            case 'grid':
                return CustomFormGrid({key: index, 
                        grid: item as IFormGrid, 
                        instanceData: instanceData,
                        onDataChanged: onDataChanged as (event: CellValueChangedEvent) => void,
                        mode: mode
                    });

            case 'label':
                return CustomFormLabel({key: index,
                    label: item as IFormLabel
                });

            case 'attachments':
                return CustomFormAttachments({key: index,
                    mode: mode,
                    attachments: attachments ?? [],
                    onAttachmentAction: onAttachmentAction,
                    onOpenCamera: onOpenCamera
                });
    
            default:
                return null
        }
    };
    
     return (
         <>
         {controls.map((o, index) => 
            renderControl(mode, o as (IBaseFormControl | IFormLabel), 
                        index, 
                        !inSection || controls.length < 2 ? 
                        {
                            flexBasis: '100%!important', 
                            maxWidth: '100%!important'
                        } : 
                        undefined
                        ))}
         </>
    );
}
